import React, { useState, useEffect, useRef } from 'react';
import NumberPillInput from './NumberPillInput';
import { Army, PlayerMeta, ArmyConfig, MetaFieldConfig, UnitMetaConfig, MetaFieldValue, UnitMetaValue } from '../armyConfig';
import { getArmyDisplayName } from '../utils/armyUtils';
import { AnimatePresence } from 'framer-motion';
import MetaTag from './MetaTag';
import { motion } from 'framer-motion';

interface ExtraInfoPanelProps {
  attackerArmy: Army | undefined;
  defenderArmy: Army | undefined;
  attackerMeta: PlayerMeta;
  defenderMeta: PlayerMeta;
  armyConfig: Record<Army, ArmyConfig>;
  onNotesChange: (player: 'Attacker' | 'Defender', value: string) => void;
  onArmyFieldChange: (player: 'Attacker' | 'Defender', key: string, value: number | boolean) => void;
  onUnitFieldChange: (player: 'Attacker' | 'Defender', unitId: string, fieldKey: string, value: number | boolean) => void;
  onUnitLabelChange: (player: 'Attacker' | 'Defender', unitId: string, label: string) => void;
  split?: boolean;
}

const ArmyFieldsPanel: React.FC<{
  fields: MetaFieldConfig[];
  values: MetaFieldValue[];
  onFieldChange: (fieldKey: string, value: number | boolean) => void;
  onePerRow?: boolean;
  marginTopFirst?: number;
}> = ({ fields, values, onFieldChange, onePerRow, marginTopFirst }) => (
  <div
    className={onePerRow ? undefined : 'army-fields-grid'}
    style={onePerRow ? { width: '100%' } : { width: '100%' }}
  >
    {fields.map((field, idx) => {
      const value = values.find(f => f.key === field.key)?.value ?? (field.type === 'integer' ? undefined : false);
      return (
        <div
          key={field.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
            width: '100%',
            minWidth: 0,
            marginBottom: onePerRow ? 8 : 0,
            marginTop: idx === 0 && marginTopFirst ? marginTopFirst : undefined,
          }}
        >
          <label style={{ flex: 1, marginRight: 0, fontSize: 16, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>{field.label}:</label>
          {field.type === 'integer' ? (
            <>
              {typeof field.default === 'number' && value !== undefined && value !== field.default && (
                <button
                  type="button"
                  aria-label={`Reset ${field.label} to default`}
                  onClick={() => onFieldChange(field.key, field.default!)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: '#222',
                    color: '#fff',
                    border: '1px solid #444',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                    opacity: 1,
                    cursor: 'pointer',
                    boxShadow: '0 1px 4px #0004',
                    transition: 'background 0.2s',
                  }}
                >
                  {/* Material circular arrows icon */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 9-9c2.39 0 4.57.94 6.21 2.47M21 12a9 9 0 0 1-9 9c-2.39 0-4.57-.94-6.21-2.47"/></svg>
                </button>
              )}
              <NumberPillInput
                value={value as number | undefined}
                onChange={v => onFieldChange(field.key, v)}
                min={field.min}
                max={field.max}
                ariaLabel={field.label}
                disablePresets={field.key !== 'score'}
              />
            </>
          ) : field.type === 'boolean' ? (
            <input
              type="checkbox"
              checked={!!value}
              onChange={e => onFieldChange(field.key, e.target.checked)}
              style={{ width: 20, height: 20 }}
            />
          ) : null}
        </div>
      );
    })}
  </div>
);

const UnitAccordion: React.FC<{
  units: UnitMetaValue[];
  configUnits: UnitMetaConfig[];
  onUnitFieldChange: (unitId: string, fieldKey: string, value: number | boolean) => void;
  onUnitLabelChange: (unitId: string, label: string) => void;
}> = ({ units, configUnits, onUnitFieldChange, onUnitLabelChange }) => {
  const [openUnit, setOpenUnit] = useState<string | null>(null);
  if (!units.length) return null;
  return (
    <div style={{ marginTop: 16 }}>
      {units.map((unit, idx) => {
        const config = configUnits.find(u => u.id === unit.id);
        if (!config) return null;
        const isOpen = openUnit === unit.id;
        const isFirst = idx === 0;
        const isLast = idx === units.length - 1;
        // Tag rendering for unit meta fields
        const tags: React.ReactNode[] = [];
        config.metaFields.forEach((field) => {
          const value = unit.metaFields.find(f => f.key === field.key)?.value;
          if (field.type === 'boolean' && field.checkedTag && value === true) {
            tags.push(
              <MetaTag key={field.key} label={field.checkedTag} color={field.tagColor || '#4caf50'} />
            );
          }
          if (field.type === 'integer' && field.thresholdTags) {
            const intValue = Number(value ?? 0);
            const matchingTags = field.thresholdTags.filter(t => intValue >= t.value);
            if (field.topTagOnly && matchingTags.length > 0) {
              // Show only the highest matching threshold tag with consistent key for smooth animation
              const highestTag = matchingTags.reduce((highest, current) => 
                current.value > highest.value ? current : highest
              );
              tags.push(
                <MetaTag key={field.key} label={highestTag.tag} color={highestTag.color || '#4caf50'} />
              );
            } else {
              // Show all matching threshold tags (default behavior)
              matchingTags.forEach(t => {
                tags.push(
                  <MetaTag key={field.key + t.value} label={t.tag} color={t.color || '#4caf50'} />
                );
              });
            }
          }
        });
        return (
          <div key={unit.id} style={{ marginBottom: 0 }}>
            <div
              onClick={() => setOpenUnit(isOpen ? null : unit.id)}
              style={{
                background: isOpen ? '#333' : '#181a1b',
                color: '#fff',
                padding: isOpen ? '12px 16px' : '10px 16px',
                fontWeight: 'bold',
                fontSize: 15,
                cursor: 'pointer',
                userSelect: 'none',
                border: '1px solid #444',
                borderBottom: isOpen ? 'none' : '1px solid #444',
                borderTop: idx === 0 ? '1px solid #444' : 'none',
                marginBottom: 0,
                textAlign: 'left',
                transition: 'background 0.2s, color 0.2s, border-radius 0.2s, padding 0.2s',
                borderTopLeftRadius: isFirst ? 8 : 0,
                borderTopRightRadius: isFirst ? 8 : 0,
                borderBottomLeftRadius: (!isOpen && isLast) ? 8 : 0,
                borderBottomRightRadius: (!isOpen && isLast) ? 8 : 0,
                display: 'block',
              }}
            >
              <div style={{ fontWeight: 'bold', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {config.name}{unit.label && ` (${unit.label})`}
              </div>
              {tags.length > 0 && (
                <motion.div layout style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, minHeight: 16, transition: 'min-height 0.3s ease-in' }}>
                  <AnimatePresence mode="sync">
                    {tags}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
            <div
              style={{
                maxHeight: isOpen ? 200 : 0,
                opacity: isOpen ? 1 : 0,
                overflow: 'hidden',
                background: '#222',
                borderBottomLeftRadius: isOpen && isLast ? 8 : 0,
                borderBottomRightRadius: isOpen && isLast ? 8 : 0,
                border: isOpen ? '1px solid #444' : 'none',
                borderTop: isOpen ? 'none' : undefined,
                marginTop: 0,
                padding: isOpen ? '10px 12px' : '0 12px',
                transition: 'max-height 0.3s, opacity 0.2s, padding 0.2s, border-radius 0.2s',
              }}
            >
              {isOpen && (
                <>
                  <input
                    type="text"
                    value={unit.label || ''}
                    onChange={e => onUnitLabelChange(unit.id, e.target.value)}
                    placeholder="Identifier"
                    style={{ width: '100%', marginBottom: 8, fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', boxSizing: 'border-box' }}
                  />
                  {config.metaFields.length > 0 && (
                    <ArmyFieldsPanel
                      fields={config.metaFields}
                      values={unit.metaFields}
                      onFieldChange={(fieldKey, value) => onUnitFieldChange(unit.id, fieldKey, value)}
                      onePerRow
                      marginTopFirst={12}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ExtraInfoPanel: React.FC<ExtraInfoPanelProps> = ({
  attackerArmy,
  defenderArmy,
  attackerMeta,
  defenderMeta,
  armyConfig,
  onNotesChange,
  onArmyFieldChange,
  onUnitFieldChange,
  onUnitLabelChange,
  split = false,
}) => {
  // Dynamically determine if we should split based on height
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldSplit, setShouldSplit] = useState(split);
  useEffect(() => {
    const checkHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        setShouldSplit(height >= 600);
      }
    };
    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, []);

  if (shouldSplit) {
    // Wide layout: two equal, scrollable halves
    return (
      <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', gap: 0 }}>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16, borderBottom: '1px solid #333' }}>
          {/* Attacker section */}
          <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#fff' }}>
            {attackerArmy ? getArmyDisplayName(attackerArmy) : 'Attacker'}
          </div>
          {attackerArmy ? (
            <>
              {/* Army-level tags */}
              <motion.div 
                layout 
                style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 6, 
                  minHeight: armyConfig[attackerArmy].armyFields?.some(field => {
                    const value = attackerMeta.armyFields.find(f => f.key === field.key)?.value;
                    return (field.type === 'boolean' && field.checkedTag && value === true) ||
                           (field.type === 'integer' && field.thresholdTags && field.thresholdTags.some(t => Number(value ?? 0) >= t.value));
                  }) ? 16 : 0,
                  transition: 'min-height 0.3s ease-in' 
                }}
              >
                <AnimatePresence mode="sync">
                  {armyConfig[attackerArmy].armyFields?.map((field) => {
                    const value = attackerMeta.armyFields.find(f => f.key === field.key)?.value;
                    if (field.type === 'boolean' && field.checkedTag && value === true) {
                      return <MetaTag key={field.key} label={field.checkedTag} color={field.tagColor || '#4caf50'} />;
                    }
                    if (field.type === 'integer' && field.thresholdTags) {
                      const intValue = Number(value ?? 0);
                      const matchingTags = field.thresholdTags.filter(t => intValue >= t.value);
                      if (field.topTagOnly && matchingTags.length > 0) {
                        // Show only the highest matching threshold tag with consistent key for smooth animation
                        const highestTag = matchingTags.reduce((highest, current) => 
                          current.value > highest.value ? current : highest
                        );
                        return <MetaTag key={field.key} label={highestTag.tag} color={highestTag.color || '#4caf50'} />;
                      } else {
                        // Show all matching threshold tags (default behavior)
                        return matchingTags.map(t => (
                          <MetaTag key={field.key + t.value} label={t.tag} color={t.color || '#4caf50'} />
                        ));
                      }
                    }
                    return null;
                  })}
                </AnimatePresence>
              </motion.div>
              <ArmyFieldsPanel
                fields={armyConfig[attackerArmy].armyFields || []}
                values={attackerMeta.armyFields}
                onFieldChange={(key, value) => onArmyFieldChange('Attacker', key, value)}
                onePerRow
                marginTopFirst={12}
              />
              {(armyConfig[attackerArmy].units ?? []).length > 0 && (
                <UnitAccordion
                  units={attackerMeta.units}
                  configUnits={armyConfig[attackerArmy].units ?? []}
                  onUnitFieldChange={(unitId, fieldKey, value) => onUnitFieldChange('Attacker', unitId, fieldKey, value)}
                  onUnitLabelChange={(unitId, label) => onUnitLabelChange('Attacker', unitId, label)}
                />
              )}
              <textarea
                value={attackerMeta.notes}
                onChange={e => onNotesChange('Attacker', e.target.value)}
                rows={4}
                placeholder={armyConfig[attackerArmy].faction + ' Notes'}
                style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
              />
            </>
          ) : (
            <>
              <textarea
                value={attackerMeta.notes}
                onChange={e => onNotesChange('Attacker', e.target.value)}
                rows={4}
                placeholder="Notes"
                style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
              />
            </>
          )}
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
          {/* Defender section */}
          <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#fff' }}>
            {defenderArmy ? getArmyDisplayName(defenderArmy) : 'Defender'}
          </div>
          {defenderArmy ? (
            <>
              <motion.div 
                layout 
                style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 6, 
                  minHeight: armyConfig[defenderArmy].armyFields?.some(field => {
                    const value = defenderMeta.armyFields.find(f => f.key === field.key)?.value;
                    return (field.type === 'boolean' && field.checkedTag && value === true) ||
                           (field.type === 'integer' && field.thresholdTags && field.thresholdTags.some(t => Number(value ?? 0) >= t.value));
                  }) ? 16 : 0,
                  transition: 'min-height 0.3s ease-in' 
                }}
              >
                <AnimatePresence mode="sync">
                  {armyConfig[defenderArmy].armyFields?.map((field) => {
                    const value = defenderMeta.armyFields.find(f => f.key === field.key)?.value;
                    if (field.type === 'boolean' && field.checkedTag && value === true) {
                      return <MetaTag key={field.key} label={field.checkedTag} color={field.tagColor || '#4caf50'} />;
                    }
                    if (field.type === 'integer' && field.thresholdTags) {
                      const intValue = Number(value ?? 0);
                      const matchingTags = field.thresholdTags.filter(t => intValue >= t.value);
                      if (field.topTagOnly && matchingTags.length > 0) {
                        // Show only the highest matching threshold tag with consistent key for smooth animation
                        const highestTag = matchingTags.reduce((highest, current) => 
                          current.value > highest.value ? current : highest
                        );
                        return <MetaTag key={field.key} label={highestTag.tag} color={highestTag.color || '#4caf50'} />;
                      } else {
                        // Show all matching threshold tags (default behavior)
                        return matchingTags.map(t => (
                          <MetaTag key={field.key + t.value} label={t.tag} color={t.color || '#4caf50'} />
                        ));
                      }
                    }
                    return null;
                  })}
                </AnimatePresence>
              </motion.div>
              <ArmyFieldsPanel
                fields={armyConfig[defenderArmy].armyFields || []}
                values={defenderMeta.armyFields}
                onFieldChange={(key, value) => onArmyFieldChange('Defender', key, value)}
                onePerRow
                marginTopFirst={12}
              />
              {(armyConfig[defenderArmy].units ?? []).length > 0 && (
                <UnitAccordion
                  units={defenderMeta.units}
                  configUnits={armyConfig[defenderArmy].units ?? []}
                  onUnitFieldChange={(unitId, fieldKey, value) => onUnitFieldChange('Defender', unitId, fieldKey, value)}
                  onUnitLabelChange={(unitId, label) => onUnitLabelChange('Defender', unitId, label)}
                />
              )}
              <textarea
                value={defenderMeta.notes}
                onChange={e => onNotesChange('Defender', e.target.value)}
                rows={4}
                placeholder={armyConfig[defenderArmy].faction + ' Notes'}
                style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
              />
            </>
          ) : (
            <>
              <textarea
                value={defenderMeta.notes}
                onChange={e => onNotesChange('Defender', e.target.value)}
                rows={4}
                placeholder="Notes"
                style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
              />
            </>
          )}
        </div>
      </div>
    );
  }
  // Default (stacked) layout
  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflowY: 'auto' }}>
      {/* Attacker */}
      <div style={{ marginBottom: 16, marginTop: 16 }}>
        <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#fff' }}>
          {attackerArmy ? getArmyDisplayName(attackerArmy) : 'Attacker'}
        </div>
        {attackerArmy ? (
          <>
            {/* Army-level tags */}
            <motion.div 
              layout 
              style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 6, 
                minHeight: armyConfig[attackerArmy].armyFields?.some(field => {
                  const value = attackerMeta.armyFields.find(f => f.key === field.key)?.value;
                  return (field.type === 'boolean' && field.checkedTag && value === true) ||
                         (field.type === 'integer' && field.thresholdTags && field.thresholdTags.some(t => Number(value ?? 0) >= t.value));
                }) ? 16 : 0,
                transition: 'min-height 0.3s ease-in' 
              }}
            >
              <AnimatePresence mode="sync">
                {armyConfig[attackerArmy].armyFields?.map((field) => {
                  const value = attackerMeta.armyFields.find(f => f.key === field.key)?.value;
                  if (field.type === 'boolean' && field.checkedTag && value === true) {
                    return <MetaTag key={field.key} label={field.checkedTag} color={field.tagColor || '#4caf50'} />;
                  }
                  if (field.type === 'integer' && field.thresholdTags) {
                    const intValue = Number(value ?? 0);
                    const matchingTags = field.thresholdTags.filter(t => intValue >= t.value);
                    if (field.topTagOnly && matchingTags.length > 0) {
                      // Show only the highest matching threshold tag with consistent key for smooth animation
                      const highestTag = matchingTags.reduce((highest, current) => 
                        current.value > highest.value ? current : highest
                      );
                      return <MetaTag key={field.key} label={highestTag.tag} color={highestTag.color || '#4caf50'} />;
                    } else {
                      // Show all matching threshold tags (default behavior)
                      return matchingTags.map(t => (
                        <MetaTag key={field.key + t.value} label={t.tag} color={t.color || '#4caf50'} />
                      ));
                    }
                  }
                  return null;
                })}
              </AnimatePresence>
            </motion.div>
            <ArmyFieldsPanel
              fields={armyConfig[attackerArmy].armyFields || []}
              values={attackerMeta.armyFields}
              onFieldChange={(key, value) => onArmyFieldChange('Attacker', key, value)}
              onePerRow
              marginTopFirst={12}
            />
            {(armyConfig[attackerArmy].units ?? []).length > 0 && (
              <UnitAccordion
                units={attackerMeta.units}
                configUnits={armyConfig[attackerArmy].units ?? []}
                onUnitFieldChange={(unitId, fieldKey, value) => onUnitFieldChange('Attacker', unitId, fieldKey, value)}
                onUnitLabelChange={(unitId, label) => onUnitLabelChange('Attacker', unitId, label)}
              />
            )}
            <textarea
              value={attackerMeta.notes}
              onChange={e => onNotesChange('Attacker', e.target.value)}
              rows={4}
              placeholder={armyConfig[attackerArmy].faction + ' Notes'}
              style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
            />
          </>
        ) : (
          <textarea
            value={attackerMeta.notes}
            onChange={e => onNotesChange('Attacker', e.target.value)}
            rows={4}
            placeholder="Notes"
            style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
          />
        )}
      </div>
      {/* Divider */}
      <hr style={{ border: 0, borderTop: '1px solid #444', margin: '16px 0' }} />
      {/* Defender */}
      <div>
        <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 8, color: '#fff' }}>
          {defenderArmy ? getArmyDisplayName(defenderArmy) : 'Defender'}
        </div>
        {defenderArmy ? (
          <>
            <motion.div 
              layout 
              style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 6, 
                minHeight: armyConfig[defenderArmy].armyFields?.some(field => {
                  const value = defenderMeta.armyFields.find(f => f.key === field.key)?.value;
                  return (field.type === 'boolean' && field.checkedTag && value === true) ||
                         (field.type === 'integer' && field.thresholdTags && field.thresholdTags.some(t => Number(value ?? 0) >= t.value));
                }) ? 16 : 0,
                transition: 'min-height 0.3s ease-in' 
              }}
            >
              <AnimatePresence mode="sync">
                {armyConfig[defenderArmy].armyFields?.map((field) => {
                  const value = defenderMeta.armyFields.find(f => f.key === field.key)?.value;
                  if (field.type === 'boolean' && field.checkedTag && value === true) {
                    return <MetaTag key={field.key} label={field.checkedTag} color={field.tagColor || '#4caf50'} />;
                  }
                  if (field.type === 'integer' && field.thresholdTags) {
                    const intValue = Number(value ?? 0);
                    const matchingTags = field.thresholdTags.filter(t => intValue >= t.value);
                    if (field.topTagOnly && matchingTags.length > 0) {
                      // Show only the highest matching threshold tag with consistent key for smooth animation
                      const highestTag = matchingTags.reduce((highest, current) => 
                        current.value > highest.value ? current : highest
                      );
                      return <MetaTag key={field.key} label={highestTag.tag} color={highestTag.color || '#4caf50'} />;
                    } else {
                      // Show all matching threshold tags (default behavior)
                      return matchingTags.map(t => (
                        <MetaTag key={field.key + t.value} label={t.tag} color={t.color || '#4caf50'} />
                      ));
                    }
                  }
                  return null;
                })}
              </AnimatePresence>
            </motion.div>
            <ArmyFieldsPanel
              fields={armyConfig[defenderArmy].armyFields || []}
              values={defenderMeta.armyFields}
              onFieldChange={(key, value) => onArmyFieldChange('Defender', key, value)}
              onePerRow
              marginTopFirst={12}
            />
            {(armyConfig[defenderArmy].units ?? []).length > 0 && (
              <UnitAccordion
                units={defenderMeta.units}
                configUnits={armyConfig[defenderArmy].units ?? []}
                onUnitFieldChange={(unitId, fieldKey, value) => onUnitFieldChange('Defender', unitId, fieldKey, value)}
                onUnitLabelChange={(unitId, label) => onUnitLabelChange('Defender', unitId, label)}
              />
            )}
            <textarea
              value={defenderMeta.notes}
              onChange={e => onNotesChange('Defender', e.target.value)}
              rows={4}
              placeholder={armyConfig[defenderArmy].faction + ' Notes'}
              style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
            />
          </>
        ) : (
          <textarea
            value={defenderMeta.notes}
            onChange={e => onNotesChange('Defender', e.target.value)}
            rows={4}
            placeholder="Notes"
            style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 6, border: '1px solid #444', background: '#111', color: '#fff', marginTop: 12, boxSizing: 'border-box' }}
          />
        )}
      </div>
    </div>
  );
};

export default ExtraInfoPanel; 